import { createI18n } from 'vue-i18n';

import { DEFAULT_LOCALE, type Locale } from './locales';
// Type-only: erased at build time, so importing the Estonian tree here does not
// pull it into the entry chunk.
import type etMessages from './messages/et';

/**
 * Translation store.
 *
 * Messages are the `resources/lang/{et,ru}/*.php` arrays converted to TypeScript
 * modules, with the original key paths preserved (`pages.*`, `components.*`,
 * `generic.*`, `validation.*`), plus a new `faq.*` namespace holding the prose
 * that used to be hardcoded in Blade partials.
 *
 * The copy is the bulk of this app — around 130 kB of text per locale — so the
 * trees are loaded on demand rather than bundled into the entry chunk. An
 * Estonian visitor never downloads the Russian copy. Russian pages do load both,
 * because Estonian is the fallback locale and genuinely supplies the keys the
 * Russian files omit.
 */

/** The Estonian tree is the complete set of keys. */
export type MessageSchema = typeof etMessages;

const loaders: Record<Locale, () => Promise<{ default: unknown }>> = {
  et: () => import('./messages/et'),
  ru: () => import('./messages/ru'),
};

const loaded = new Set<Locale>();

/**
 * Seeded empty and filled by `setI18nLocale` before the app mounts. The trees
 * are declared (rather than passing `{}`) so `t()` stays typed against the
 * complete Estonian key set and `i18n.global.locale` resolves to a ref.
 */
const messages = {
  et: {} as MessageSchema,
  ru: {} as MessageSchema,
};

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: DEFAULT_LOCALE as Locale,
  fallbackLocale: DEFAULT_LOCALE,
  messages,
  // Falling back to Estonian is expected on Russian pages, so don't log it.
  missingWarn: false,
  fallbackWarn: false,
  // Much of the site copy carries inline markup — links, <b>, <i>, <span> — and
  // has done since it lived in the PHP lang files, where Blade rendered it with
  // `{!! !!}`. It is author-maintained content with no user input anywhere in
  // it, and the components that show it use `v-html` deliberately.
  warnHtmlMessage: false,
});

async function loadLocale(locale: Locale): Promise<void> {
  if (loaded.has(locale)) {
    return;
  }
  const module = await loaders[locale]();
  i18n.global.setLocaleMessage(locale, module.default as MessageSchema);
  loaded.add(locale);
}

/**
 * Switches locale, fetching its messages first. Must be awaited before the
 * affected component renders, or the first paint shows raw key paths.
 */
export async function setI18nLocale(locale: Locale): Promise<void> {
  await loadLocale(locale);

  // The fallback tree has to be present too, since the Russian files omit the
  // Estonian-only study pages and rely on falling through.
  if (locale !== DEFAULT_LOCALE) {
    await loadLocale(DEFAULT_LOCALE);
  }

  i18n.global.locale.value = locale;
}

export default i18n;
